import type { BattleEvent } from "../core/types";

export class GameAudio {
  readonly #volume: number;
  readonly #lastPlayed = new Map<string, number>();
  #context: AudioContext | null = null;
  #master: GainNode | null = null;

  constructor(masterVolumePercent: number) {
    this.#volume = Math.max(0, Math.min(1, masterVolumePercent / 100));
    if (this.#volume <= 0) return;
    try {
      this.#context = new AudioContext();
      this.#master = this.#context.createGain();
      this.#master.gain.value = this.#volume * 0.22;
      this.#master.connect(this.#context.destination);
    } catch {
      this.#context = null;
      this.#master = null;
    }
  }

  consume(events: readonly BattleEvent[]): void {
    if (!this.#context || !this.#master) return;
    if (this.#context.state === "suspended") {
      void this.#context.resume().catch(() => undefined);
    }

    for (const event of events) {
      if (event.type === "shot" && this.#canPlay("shot", 55)) {
        this.#tone(330, 0.045, "square", 0.15, 250);
      } else if (event.type === "critical-hit" && this.#canPlay("critical", 90)) {
        this.#chord([740, 988], 0.11, "square", 0.22);
      } else if (event.type === "enemy-hit" && this.#canPlay("hit", 42)) {
        this.#tone(150, 0.035, "triangle", 0.13, 105);
      } else if (event.type === "enemy-killed" && this.#canPlay("kill", 70)) {
        this.#tone(190, 0.075, "sawtooth", 0.17, 330);
      } else if (event.type === "pickup" && this.#canPlay("pickup", 55)) {
        this.#tone(620, 0.07, "sine", 0.16, 900);
      } else if (event.type === "player-hurt") {
        this.#tone(105, 0.18, "sawtooth", 0.34, 58);
      } else if (event.type === "level-up") {
        this.#chord([440, 554, 659], 0.28, "triangle", 0.18);
      } else if (event.type === "purchase") {
        this.#chord([410, 615], 0.16, "sine", 0.16);
      } else if (event.type === "weapon-merged") {
        this.#chord([330, 495, 660], 0.24, "triangle", 0.2);
      } else if (event.type === "boss-spawned") {
        this.#chord([82, 103, 123], 0.7, "sawtooth", 0.22);
      } else if (event.type === "countdown") {
        this.#tone(event.seconds === 1 ? 660 : 440, 0.1, "square", 0.2, 360);
      } else if (event.type === "wave-cleared") {
        this.#chord([392, 523, 659], 0.34, "triangle", 0.2);
      } else if (event.type === "wave-start") {
        this.#chord([294, 392], 0.22, "square", 0.13);
      } else if (event.type === "phase-change" && event.phase === "loot") {
        this.#chord([330, 440, 554], 0.2, "sine", 0.17);
      } else if (event.type === "phase-change" && event.phase === "shop") {
        this.#chord([220, 277, 330], 0.26, "triangle", 0.13);
      } else if (event.type === "phase-change" && event.phase === "victory") {
        this.#chord([392, 494, 587, 784], 0.75, "triangle", 0.3);
      } else if (event.type === "phase-change" && event.phase === "gameover") {
        this.#chord([196, 147, 110], 0.7, "sawtooth", 0.24);
      }
    }
  }

  destroy(): void {
    if (this.#context) void this.#context.close().catch(() => undefined);
    this.#context = null;
    this.#master = null;
  }

  #canPlay(key: string, minimumGapMs: number): boolean {
    const now = performance.now();
    const previous = this.#lastPlayed.get(key) ?? -minimumGapMs;
    if (now - previous < minimumGapMs) return false;
    this.#lastPlayed.set(key, now);
    return true;
  }

  #chord(
    frequencies: readonly number[],
    duration: number,
    type: OscillatorType,
    volume: number,
  ): void {
    frequencies.forEach((frequency, index) => {
      this.#tone(
        frequency,
        duration + index * 0.025,
        type,
        volume / frequencies.length,
        frequency * 1.08,
        index * 0.018,
      );
    });
  }

  #tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    endFrequency: number,
    delay = 0,
  ): void {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master) return;
    const start = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(20, frequency), start);
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(20, endFrequency),
      start + duration,
    );
    gain.gain.setValueAtTime(Math.max(0.0001, volume), start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(master);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  }
}
